import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { SupportedCurrencies } from '../../../../../core/constants/currency';
import { OrdersService } from '../../../../../core/services/orders.service';
import { AppUserClaimsService } from '../../../../../core/services/app-user-claims.service';
import { environment } from '../../../../../../environments/environment';
import { SpinnerService } from '../../../../../core/services/spinner.service';
import { AuthService } from '../../../../../core/services/auth.service';
import { Account } from '@mitel/cloudlink-sdk';

@Component({
  selector: 'free-trial-dialog',
  templateUrl: './free-trial-dialog.component.html',
  styleUrls: ['./free-trial-dialog.component.scss'],
})

export class FreeTrialDialogComponent implements OnInit {
	ftFormGroup: FormGroup;
	disableSaveButton: boolean = true;
	currencyCode: string = '';
	currencyNeeded: boolean = false;
	createFreeTrial: boolean = true;
	supportedCodes: string[] = SupportedCurrencies;
	noBillingAccount: boolean = false;
	showFormError: boolean = false;
	errMsg: string = "";
	errMsgStyle: any = { 'max-width': '500px' };
	legalUrl: string;
	maxLicenses: number = 10000;

	constructor( @Inject(MAT_DIALOG_DATA) public data: any,
				public dialogRef: MatDialogRef<FreeTrialDialogComponent>,
				private translateService: TranslateService,
				private domSanitizer: DomSanitizer,
				private formBuilder: FormBuilder,
				private matIconRegistry: MatIconRegistry,
				private orderService: OrdersService,
				private appClaimsService: AppUserClaimsService,
				private spinnerService: SpinnerService,
				private authService: AuthService ) 
	{
		console.log("CONSTRUCTOR: Free trial dialog", this.data)
		this.createFreeTrial = true;
		this.matIconRegistry.addSvgIcon(
			'trash_can',
			this.domSanitizer.bypassSecurityTrustResourceUrl("../assets/images/trash.svg")
		);
	}

	ngOnInit() 
	{
		console.log("ngOnInit: Free trial dialog", this.data)
		if ( this.data.subInfo && this.data.subInfo.maxLicenses != undefined )
			this.maxLicenses = this.data.subInfo.maxLicenses;

		this.initForm();
		// first check to see what state the integration is in with respect to this account
		this.determineInitalDialogState( this.data.account );
	}

	initForm()
	{
		this.ftFormGroup = this.formBuilder.group ({
			numLicensesField: ['', [Validators.min(1), Validators.max(this.maxLicenses)]]
		});
	} 

	numberOnly(event): boolean 
	{
		const charCode = (event.which) ? event.which : event.keyCode;
		if (charCode > 31 && (charCode < 48 || charCode > 57)) {
		  return false;
		}
		return true;
	}

	determineInitalDialogState( account: Account )
	{
		console.log("determineInitalDialogState", this.data)
		if ( this.data.createBillingAccount == true && this.appClaimsService.isAssumedRole() == false )
		{
			//this is the case where there s an account admin. They can not create their free trial if the
			// partner does not have an account
			this.createFreeTrial = false;
			this.noBillingAccount = true;
			return;
		}
		this.createFreeTrial = true;
		this.currencyNeeded = this.data.createBillingAccount;
		this.legalUrl = this.data.subInfo.eulaUrl;

		// currency is only displayed if a billing account will be created
		if ( this.currencyNeeded )
		{
			this.ftFormGroup.addControl('currencyCodeField', new FormControl(''));
		}
		if ( this.legalUrl )
		{
			this.ftFormGroup.addControl('termConditionField', new FormControl('', [Validators.requiredTrue]));
		}
	}

	handleCodeChange(currencyCode)
	{
		this.showFormError = false;
		this.currencyCode = currencyCode.value;
		console.log("current code", this.currencyCode)
		this.enableDisableSaveButton();			
	}

	handleLicenceChange(value)
	{
		this.ftFormGroup.updateValueAndValidity();
		this.enableDisableSaveButton();	
	}

	handleTermsChange(event)
	{
		this.ftFormGroup.updateValueAndValidity();
		this.enableDisableSaveButton();	
	}

	enableDisableSaveButton()
	{
		if ( (this.currencyCode == null || this.currencyCode == undefined || this.currencyCode == "") && this.currencyNeeded )
			this.disableSaveButton = true;
		else if ( this.ftFormGroup.controls['numLicensesField'].value == undefined || 
			this.ftFormGroup.controls['numLicensesField'].value == '' || 
			this.ftFormGroup.controls['numLicensesField'].errors )
			this.disableSaveButton = true;
		else if ( this.legalUrl && this.ftFormGroup.controls['termConditionField'].value == false )
			this.disableSaveButton = true;
		else
			this.disableSaveButton = false;
	}

	onRemoveIntegration()
	{
		console.log("Remove the integration - when successful, close the dialog");
		let result = 
		{
			remove: true,
		}
		this.dialogRef.close( result);
	}

	onSave(event)
	{	
		this.showFormError = false;
		console.log("Save the changes to the integration - when successful, close the dialog", this.currencyCode);
		this.spinnerService.setSpinnerMessageByKey('free-trial.creating-free-trial');
		this.spinnerService.showSpinner();
		let quantity: number = +this.ftFormGroup.controls['numLicensesField'].value;
		if ( this.currencyNeeded ) // needs to create a billing account first
		{
			return this.authService.revertRole().then( token => {
				return this.orderService.createBillingAccount(true, this.currencyCode).then(billingAcct => {
					return this.authService.assumeRole("ACCOUNT_ADMIN", this.data.account.accountId).then( newtoken => {
						return this.orderService.assignFreeTrialToCustomerAccount(this.data.account,
								this.data.subInfo.partnerAccountId,
								this.data.subInfo.productRatePlanChargeId,
								this.data.subInfo.productRatePlanId,
								this.data.subInfo.price,
								quantity,
								this.data.subInfo.period,
								this.data.subInfo.periodType).then(done => {
							this.spinnerService.hideSpinner();
							console.log("Free trial assigned, close the dialog");
							let result = 
							{
								freeTrialComplete: true,
							}
							this.dialogRef.close( result);
							return;
						});
					});
				});
			}).catch( async err => { 
				this.spinnerService.hideSpinner();
				console.error("Error creating billing account or free trial order", err, this.data.account);
				if ( this.appClaimsService.isAssumedRole() == false ) // need to restore the role
					await this.authService.assumeRole("ACCOUNT_ADMIN", this.data.account.accountId)
				this.displayErrorBasedOnException(err);
			});
		}
		else
		{
			return this.orderService.assignFreeTrialToCustomerAccount(this.data.account,
					this.data.subInfo.partnerAccountId,
					this.data.subInfo.productRatePlanChargeId,
					this.data.subInfo.productRatePlanId,
					this.data.subInfo.price,
					quantity,
					this.data.subInfo.period,
					this.data.subInfo.periodType).then(done => {
				this.spinnerService.hideSpinner();
				console.log("Free trial assigned, close the dialog");
				let result = 
				{
					freeTrialComplete: true,
				}
				this.dialogRef.close( result);
				return;
			}).catch( async err => { 
				this.spinnerService.hideSpinner();
				console.error("Error creating billing account or free trial order", err, this.data.account);
				this.displayErrorBasedOnException(err);
			});
		}
	}

	onDone()
	{
		let result = 
		{
			setupComplete: false,
		}
		this.dialogRef.close( result);
	}

	displayErrorBasedOnException( err: any )
	{
		console.error("Display this error to the user", err);
		if ( err.body && err.body.message )
		{
			if ( err.body.message.indexOf("is no billing account") > -1 )
			{
				// this can happen if the partner does not have an account and the admin is adding a trial
				this.noBillingAccount = true;
				this.createFreeTrial = false;
				return;
			}
			else if ( err.body.message.indexOf("Country is a required field for Sold To Contact") >= 0 )
			{
				this.errMsg = this.translateService.instant('integrations.contact-center-config.generic-billing-error') + ": " +
					this.translateService.instant('integrations.contact-center-config.needs-address-error');
			}
			else if ( err.body.message.indexOf("Zuora request failed: Taxation Requirement: State is required for Sold To Contact") >= 0 )
			{
				this.errMsg = this.translateService.instant('integrations.contact-center-config.generic-billing-error') + ": " +
					this.translateService.instant('integrations.contact-center-config.needs-address-error');
			}
			else 
			{
				this.errMsg = this.translateService.instant('integrations.contact-center-config.generic-billing-error') + ": " +
					err.body.message;
			}
		}
		else
		{
			this.errMsg = this.translateService.instant('integrations.contact-center-config.generic-billing-error');
		}
		console.log("Displaying this error message to the user", this.errMsg);
		this.showFormError = true;
	}
}
